// apps/web/src/lib/vendas/importacao.ts — Fase 12, Plano 25 (PV-03, D-30..D-32).
// Transformações puras do assistente de importação de planilha (rótulos, regras de confirmação,
// filtro e achatamento da prévia). Nenhuma regra de negócio: o servidor reprocessa o arquivo (T-12-27).
import type {
  CampoImportacaoPv,
  GrupoPreviaImportacao,
  LinhaPreviaImportacao,
  MapeamentoImportacaoPv,
  PreviaImportacao,
} from '@pieltec/contracts';
import { CAMPOS_OBRIGATORIOS_IMPORTACAO_PV } from '@pieltec/contracts';

/** Coluna escolhida por campo: índice em texto (valor do `select`) ou '' para "Selecionar coluna". */
export type ColunasEscolhidas = Partial<Record<CampoImportacaoPv, string>>;

export const CAMPOS_ROTULOS: ReadonlyArray<{ campo: CampoImportacaoPv; rotulo: string }> = [
  { campo: 'numeroPedido', rotulo: 'Nº do pedido (agrupa os PVs)' },
  { campo: 'codigoItemCliente', rotulo: 'Código do item do cliente *' },
  { campo: 'descricao', rotulo: 'Descrição' },
  { campo: 'cor', rotulo: 'Cor' },
  { campo: 'quantidade', rotulo: 'Quantidade *' },
  { campo: 'unidade', rotulo: 'Unidade' },
  { campo: 'preco', rotulo: 'Preço' },
  { campo: 'prazo', rotulo: 'Prazo / previsão' },
];

export const LIMITE_ARQUIVO_BYTES = 10 * 1024 * 1024;
export const ERRO_ARQUIVO_INVALIDO = 'Não foi possível ler o arquivo. Use uma planilha .xlsx ou .csv com cabeçalhos na primeira linha.';
export const ERRO_ARQUIVO_GRANDE = 'O arquivo tem mais de 10 MB. Use uma planilha de até 10 MB.';
export const MSG_SEM_LINHAS = 'A planilha não tem linhas de itens para importar.';
export const AVISO_SEM_NUMERO_PEDIDO = 'Sem a coluna "Nº do pedido", todas as linhas viram um único PV.';

export function rotuloCriarPvs(quantidade: number): string {
  return quantidade === 1 ? 'Criar 1 PV' : `Criar ${quantidade} PVs`;
}

export function rotuloResumoPrevia(resumo: PreviaImportacao['resumo']): string {
  return `${resumo.pvs} PV(s) · ${resumo.itens} itens · ${resumo.comErro} com erro · ${resumo.itensNovos} itens novos`;
}

export function rotuloSucessoImportacao({ pvs, aguardandoVinculo }: { pvs: number; aguardandoVinculo: number }): string {
  return `${pvs} PV(s) criado(s). ${aguardandoVinculo} itens aguardam vínculo de material.`;
}

/** Texto do status da linha. O servidor já inclui "(linha n)" nos motivos de erro; só completa se faltar. */
export function rotuloLinhaPrevia(linha: LinhaPreviaImportacao): string {
  if (linha.status === 'ERRO') {
    const motivo = linha.motivo ?? 'Linha inválida';
    return /\(linha \d+\)/.test(motivo) ? motivo : `${motivo} (linha ${linha.linha})`;
  }
  if (linha.status === 'ITEM_NOVO') return linha.motivo ?? 'Item novo — material não vinculado';
  return 'OK';
}

/** Regra de liberação do botão final: nunca sem itens válidos; com erros, só com a caixa marcada. */
export function podeConfirmar({ comErro, ignorarErros = false, itensValidos }: { comErro: number; ignorarErros?: boolean; itensValidos: number }): boolean {
  if (itensValidos <= 0) return false;
  return comErro === 0 || ignorarErros;
}

export function filtrarLinhasComProblema(grupos: readonly GrupoPreviaImportacao[]): GrupoPreviaImportacao[] {
  return grupos
    .map((grupo) => ({ ...grupo, linhas: grupo.linhas.filter((linha) => linha.status !== 'OK') }))
    .filter((grupo) => grupo.linhas.length > 0);
}

export type LinhaAchatada =
  | { readonly tipo: 'grupo'; readonly chave: string; readonly titulo: string }
  | { readonly tipo: 'linha'; readonly chave: string; readonly linha: LinhaPreviaImportacao };

/** Lista única (cabeçalho de grupo + linhas) para renderizar/virtualizar a prévia agrupada. */
export function achatarGrupos(grupos: readonly GrupoPreviaImportacao[]): LinhaAchatada[] {
  const saida: LinhaAchatada[] = [];
  grupos.forEach((grupo, indice) => {
    saida.push({
      tipo: 'grupo',
      chave: `g-${indice}`,
      titulo: `PV a criar: ${grupo.numeroPedido ?? 'sem nº do pedido'} · ${grupo.linhas.length} itens`,
    });
    for (const linha of grupo.linhas) saida.push({ tipo: 'linha', chave: `l-${indice}-${linha.linha}`, linha });
  });
  return saida;
}

export function colunasIniciais(salvo: MapeamentoImportacaoPv | null | undefined): ColunasEscolhidas {
  const inicial: ColunasEscolhidas = {};
  if (!salvo) return inicial;
  for (const { campo } of CAMPOS_ROTULOS) {
    const coluna = salvo[campo];
    if (coluna) inicial[campo] = String(coluna.indice);
  }
  return inicial;
}

export function montarMapeamento(colunas: ColunasEscolhidas, cabecalhos: readonly string[], temCabecalho: boolean): MapeamentoImportacaoPv {
  const saida: MapeamentoImportacaoPv = {};
  for (const { campo } of CAMPOS_ROTULOS) {
    const escolhido = colunas[campo];
    if (escolhido === undefined || escolhido === '') continue;
    const indice = Number(escolhido);
    saida[campo] = { indice, cabecalho: temCabecalho ? (cabecalhos[indice] ?? null) : null };
  }
  return saida;
}

export function mapeamentoObrigatorioCompleto(colunas: ColunasEscolhidas): boolean {
  return CAMPOS_OBRIGATORIOS_IMPORTACAO_PV.every((campo) => (colunas[campo] ?? '') !== '');
}

export function rotuloColuna(cabecalho: string, indice: number): string {
  return cabecalho.trim() === '' ? `Coluna ${indice + 1}` : cabecalho;
}

/** Falha ao confirmar a importação: só 4xx garante que o servidor NÃO gravou (a transação foi desfeita). */
export type SituacaoFalhaConfirmacao = 'NADA_GRAVADO' | 'INCERTO';

/**
 * `status` null = falha de rede/timeout (a resposta não chegou); 5xx também: o servidor pode ter commitado antes de a
 * resposta se perder (WR-D1-01). Só 4xx explícito (400/403/404/409/422...) garante que nada foi gravado.
 */
export function classificarFalhaConfirmacao(status: number | null): SituacaoFalhaConfirmacao {
  return status !== null && status >= 400 && status < 500 ? 'NADA_GRAVADO' : 'INCERTO';
}

export const MSG_CONFIRMACAO_INCERTA =
  'Não foi possível confirmar se os PVs foram criados (falha de conexão ou do servidor). Confira a lista de pedidos antes de tentar novamente; se o arquivo já tiver sido importado, o sistema avisa e não duplica.';

/** Texto do erro ao confirmar: "Nada foi gravado" só para 4xx; rede/5xx manda conferir a lista de pedidos. */
export function mensagemFalhaConfirmacao(status: number | null, detalhe: string | null): string {
  if (classificarFalhaConfirmacao(status) === 'INCERTO') return MSG_CONFIRMACAO_INCERTA;
  return `${detalhe ?? 'Não foi possível criar os PVs.'} Nada foi gravado; revise e tente novamente.`;
}
